import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useOutletContext } from 'react-router-dom';
import { applyTheme } from '@/lib/theme';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import ImageUploader from '../components/ImageUploader';

const COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#EF4444', '#06B6D4', '#0F172A'];

export default function AssoParametres() {
  const { asso, setAsso } = useOutletContext();
  const [form, setForm] = useState({
    nom: '', couleur_principale: '', logo_url: '', domaine: '',
    email_contact: '', telephone: '', adresse: '', ville: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (asso) {
      setForm({
        nom: asso.nom || '',
        couleur_principale: asso.couleur_principale || '#10B981',
        logo_url: asso.logo_url || '',
        domaine: asso.domaine || '',
        email_contact: asso.email_contact || '',
        telephone: asso.telephone || '',
        adresse: asso.adresse || '',
        ville: asso.ville || '',
      });
    }
  }, [asso]);

  const upd = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === 'couleur_principale') applyTheme(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put('/assos/me', form);
      setAsso({ ...asso, ...data });
      applyTheme(form.couleur_principale);
      toast.success('Paramètres enregistrés');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  if (!asso) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-3xl mx-auto" data-testid="asso-parametres">
      <div className="mb-8 slide-up">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Paramètres</div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">Personnalisez votre espace.</h1>
        <p className="text-[#475569] mt-2">Vos modifications s'appliquent immédiatement à tous les membres.</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <section className="card">
          <h2 className="font-display text-lg font-bold text-[#0F172A] mb-5">Identité</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label-uppercase block mb-2">Nom de l'association</label>
              <input type="text" required value={form.nom} onChange={(e) => upd('nom', e.target.value)} className="input-base" data-testid="params-nom" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-uppercase block mb-2">Couleur principale</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => upd('couleur_principale', c)}
                    className={`w-10 h-10 rounded-lg transition-all ${form.couleur_principale === c ? 'ring-2 ring-offset-2 ring-slate-900 scale-110' : ''}`}
                    style={{ background: c }}
                    data-testid={`params-color-${c.slice(1)}`}
                  />
                ))}
                <input
                  type="color"
                  value={form.couleur_principale}
                  onChange={(e) => upd('couleur_principale', e.target.value)}
                  className="w-10 h-10 rounded-lg cursor-pointer border border-[#E2E8F0]"
                  data-testid="params-color-custom"
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <ImageUploader value={form.logo_url} onChange={(url) => upd('logo_url', url)} label="Logo (optionnel)" testid="params-logo" aspect="1/1" />
            </div>
          </div>
        </section>

        <section className="card">
          <h2 className="font-display text-lg font-bold text-[#0F172A] mb-5">Contact & Domaine</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-uppercase block mb-2">Email contact</label>
              <input type="email" value={form.email_contact} onChange={(e) => upd('email_contact', e.target.value)} className="input-base" />
            </div>
            <div>
              <label className="label-uppercase block mb-2">Téléphone</label>
              <input type="tel" value={form.telephone} onChange={(e) => upd('telephone', e.target.value)} className="input-base" />
            </div>
            <div>
              <label className="label-uppercase block mb-2">Ville</label>
              <input type="text" value={form.ville} onChange={(e) => upd('ville', e.target.value)} className="input-base" />
            </div>
            <div>
              <label className="label-uppercase block mb-2">Domaine personnalisé</label>
              <input type="text" value={form.domaine} onChange={(e) => upd('domaine', e.target.value)} className="input-base" placeholder="monasso.fr" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-uppercase block mb-2">Adresse</label>
              <input type="text" value={form.adresse} onChange={(e) => upd('adresse', e.target.value)} className="input-base" />
            </div>
          </div>
        </section>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="params-save">
            {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
          </button>
        </div>
      </form>
    </div>
  );
}
